import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';
import Icon, { IconName } from '../ui/Icon';
import Text from '../ui/Text';

/**
 * The shape every end-of-payment screen shares: a badge, a verdict, the
 * facts, and what to do next.
 *
 * Five screens use it - verifying, paid, failed, expired, refunded - and they
 * differ only in ink, glyph and copy. Written once because the alternative is
 * five hand-built pages that drift: the failure screen ends up with the order
 * id in a different place from the success screen, and a customer comparing
 * two of them in a support chat cannot find the same number twice.
 *
 * Presentational only. Every decision about which of the five is showing, and
 * every timer behind it, belongs to `PaymentScreen`.
 */

export type ResultTone = 'success' | 'danger' | 'neutral';

export type ResultRow = {
  label: string;
  value: string;
  /** A chip instead of plain text - "Refund Initiated". */
  badge?: boolean;
};

type Props = {
  tone: ResultTone;
  icon: IconName;
  title: string;
  /** One or two lines under the title. */
  blurb: string;
  /** The facts card: amount, order id, when, how. */
  rows?: ResultRow[];
  /** A tinted panel carrying a reason and its code. */
  notice?: {
    title: string;
    detail: string;
    /** The gateway's own code, which support will ask for. */
    code?: string;
  };
  /** Anything between the facts and the buttons - the verification steps. */
  children?: React.ReactNode;
  /** A last quiet line at the foot, above the actions. */
  note?: string;
  /** The buttons. Passed in so each screen owns its own next steps. */
  actions?: React.ReactNode;
};

const TINTS: Record<ResultTone, { fill: string; ink: string }> = {
  success: { fill: colors.successSurface, ink: colors.success },
  danger: { fill: colors.dangerSurface, ink: colors.danger },
  neutral: { fill: colors.surfaceMuted, ink: colors.primary },
};

function PaymentResult({
  tone,
  icon,
  title,
  blurb,
  rows,
  notice,
  children,
  note,
  actions,
}: Props) {
  const tint = TINTS[tone];
  const insets = useSafeAreaInsets();

  return (
    /* Pads for the status bar itself, like `PageHeader`: the payment flow is
       mounted without a top safe-area edge so the header is not inset twice. */
    <View style={[styles.root, { paddingTop: insets.top }]}>
      <ScrollView
        contentContainerStyle={styles.page}
        showsVerticalScrollIndicator={false}
      >
        {/*
          A disc inside a wider ring of the same tint.

          The ring is what stops an 84pt circle of pale green from reading as
          an empty avatar slot. It is also the only ornament on these pages -
          a result screen is read once, in a hurry, and anything else on it
          competes with the one word that matters.
        */}
        <View style={[styles.ring, { backgroundColor: tint.fill }]}>
          <View style={[styles.badge, { backgroundColor: colors.surface }]}>
            <Icon name={icon} size={40} color={tint.ink} strokeWidth={1.5} />
          </View>
        </View>

        <Text variant="title" align="center" color={tint.ink}>
          {title}
        </Text>
        <Text variant="bodySm" align="center">
          {blurb}
        </Text>

        {rows?.length ? (
          <View style={styles.card}>
            {rows.map((row, index) => (
              <View
                key={row.label}
                style={[styles.row, index > 0 ? styles.rowRuled : null]}
              >
                <Text variant="caption" style={styles.rowLabel}>
                  {row.label}
                </Text>

                {row.badge ? (
                  <View style={[styles.chip, { backgroundColor: tint.fill }]}>
                    <Text variant="eyebrow" color={tint.ink}>
                      {row.value}
                    </Text>
                  </View>
                ) : (
                  <Text variant="labelSm" style={styles.rowValue} numberOfLines={1}>
                    {row.value}
                  </Text>
                )}
              </View>
            ))}
          </View>
        ) : null}

        {notice ? (
          <View style={[styles.notice, { backgroundColor: tint.fill }]}>
            <Text variant="eyebrow" color={colors.inkMuted}>
              Failure Reason
            </Text>
            <Text variant="labelSm">{notice.title}</Text>
            <Text variant="bodySm" color={colors.inkBody}>
              {notice.detail}
            </Text>

            {notice.code ? (
              <>
                <Text variant="eyebrow" color={colors.inkMuted} style={styles.codeLabel}>
                  Error Code
                </Text>
                {/* Selectable: the first thing support asks for is this
                    string, and reading a code aloud gets it wrong. */}
                <Text variant="labelSm" selectable>
                  {notice.code}
                </Text>
              </>
            ) : null}
          </View>
        ) : null}

        {children}

        {note ? (
          <View style={styles.note}>
            <Icon name="shieldCheck" size={14} color={colors.inkMuted} />
            <Text variant="caption" style={styles.noteText}>
              {note}
            </Text>
          </View>
        ) : null}
      </ScrollView>

      {/*
        The buttons clear the gesture bar or the three nav buttons themselves.

        The screen is rendered inside a `SafeAreaView` that claims only the top
        edge - the tab bar normally owns the bottom - so nothing above this was
        padding for it, and "Go to Home" was sitting underneath Android's back
        button. `spacing.md` is the floor, for a device with no inset at all.
      */}
      {actions ? (
        <View
          style={[
            styles.actions,
            { paddingBottom: Math.max(insets.bottom, spacing.md) },
          ]}
        >
          {actions}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  page: {
    flexGrow: 1,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xxxl,
    paddingBottom: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
  },
  ring: {
    width: 108,
    height: 108,
    borderRadius: 54,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  badge: {
    width: 82,
    height: 82,
    borderRadius: 41,
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    alignSelf: 'stretch',
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.md,
  },
  rowRuled: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  rowLabel: {
    flexShrink: 0,
  },
  rowValue: {
    flexShrink: 1,
    textAlign: 'right',
  },
  chip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
  },
  notice: {
    alignSelf: 'stretch',
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: radii.lg,
    gap: spacing.xs,
  },
  codeLabel: {
    marginTop: spacing.sm,
  },
  note: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    gap: spacing.sm,
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  noteText: {
    flex: 1,
  },
  actions: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    gap: spacing.sm,
  },
});

export default PaymentResult;
